import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { feedbackApi } from '../../api/feedback';
import { DEFAULT_PARAMS, MIN_SEARCH_LENGTH, parseFeedbackParams, toApiParams, toQueryString, updateParams } from './feedbackParams';

const SEARCH_DEBOUNCE_MS = 300;
export const feedbackListKey = (apiParams) => ['feedback-list', apiParams];
export const feedbackDetailKey = (id) => ['feedback', id];

const shouldRetry = (count, error) => (error?.status === 0 || error?.status >= 500) && count < 1;

/** Feedback list, synced with the URL (see feedbackParams.js). */
export default function useFeedback() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { params, invalid } = useMemo(() => parseFeedbackParams(searchParams), [searchParams]);
  const apiParams = useMemo(() => toApiParams(params), [params]);

  const writeParams = useCallback(
    (next, { replace = false } = {}) => setSearchParams(toQueryString(next), { replace }),
    [setSearchParams],
  );

  useEffect(() => {
    if (invalid.length) writeParams(params, { replace: true });
  }, [invalid, params, writeParams]);

  const setParams = useCallback((changes, options) => writeParams(updateParams(params, changes), options), [params, writeParams]);

  // --- debounced search box; 1 character means "no search" ---
  const [searchInput, setSearchInput] = useState(params.search);
  const lastApplied = useRef(params.search);
  useEffect(() => {
    if (params.search !== lastApplied.current) {
      lastApplied.current = params.search;
      setSearchInput(params.search);
    }
  }, [params.search]);

  useEffect(() => {
    const trimmed = searchInput.trim();
    const term = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : '';
    if (term === params.search) return undefined;
    const timer = setTimeout(() => {
      lastApplied.current = term;
      setParams({ search: term });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, params.search, setParams]);

  const clearFilters = useCallback(() => {
    setSearchInput('');
    lastApplied.current = '';
    const { search, status, category, source, days, from, to } = DEFAULT_PARAMS;
    setParams({ search, status, category, source, days, from, to });
  }, [setParams]);

  // --- data ---
  const query = useQuery({
    queryKey: feedbackListKey(apiParams),
    queryFn: ({ signal }) => feedbackApi.getFeedbackList(apiParams, { signal }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: shouldRetry,
  });
  const { data, error, isPending, isFetching, isPlaceholderData, refetch } = query;

  // Past the last page: go to the last one that exists
  useEffect(() => {
    if (!data || isPlaceholderData) return;
    const lastPage = Math.max(1, data.pagination.totalPages);
    if (params.page > lastPage) writeParams({ ...params, page: lastPage }, { replace: true });
  }, [data, isPlaceholderData, params, writeParams]);

  // 400: show the message, reset the offending params once and retry
  const resetFor = useRef(null);
  useEffect(() => {
    if (error?.status !== 400) return;
    const key = toQueryString(params);
    if (resetFor.current === key) return;
    resetFor.current = key;
    toast.error(error.message);
    const fields = error.fieldErrors.map((e) => (e.field === 'to' ? 'from' : e.field)).filter((f) => f in DEFAULT_PARAMS);
    const reset = fields.length
      ? Object.fromEntries(fields.flatMap((f) => (f === 'from' ? [['from', ''], ['to', '']] : [[f, DEFAULT_PARAMS[f]]])))
      : { ...DEFAULT_PARAMS, id: params.id };
    writeParams({ ...params, ...reset }, { replace: true });
  }, [error, params, writeParams]);

  return {
    params,
    setParams,
    searchInput,
    setSearchInput,
    clearFilters,
    rows: data?.feedback ?? [],
    counts: data?.counts ?? null,
    pagination: data?.pagination ?? null,
    isInitialLoading: isPending && !data,
    isRefreshing: isFetching && isPlaceholderData,
    isFetching,
    error: data && !isPlaceholderData ? null : error,
    refetch,
  };
}
